import React, { useEffect } from 'react';

import SheetContainer, { Content, Overlay, Sheet } from '../../../mobile/structural/sheet';
import useSheet from '../../../mobile/structural/sheet/hooks/useSheet';
import { SheetId } from '../../../mobile/structural/sheet/sheetTypes';

type ChipSelectionSheetProps = {
    sheetId: SheetId;
    instanceId?: string;
    className?: string;
    isOpen?: boolean;
    onOpenChange: (isOpen: boolean) => void;
    onCloseTransitionStart?: () => void;
    children: React.ReactNode | ((close: () => void) => React.ReactNode);
};

const renderChildren = (children: ChipSelectionSheetProps['children'], close: () => void) =>
    typeof children === 'function' ? children(close) : children;

/**
 * Renders a mobile sheet whose open state is driven by generic chip selection.
 */
const ChipSelectionSheet: React.FC<ChipSelectionSheetProps> = ({
    sheetId,
    instanceId,
    className,
    isOpen,
    onOpenChange,
    onCloseTransitionStart,
    children,
}) => {
    const sheetProps = useSheet(sheetId, instanceId);
    const close = () => onOpenChange(false);

    useEffect(() => {
        if (isOpen) {
            sheetProps.dispatchOpenSheet();
            return;
        }

        sheetProps.dispatchCloseSheet();
    }, [isOpen]);

    const handleCloseTransitionStart = () => {
        onCloseTransitionStart?.();
        close();
    };

    return (
        <SheetContainer {...sheetProps} className={className} onCloseTransitionStart={handleCloseTransitionStart}>
            <Overlay />
            <Sheet>
                <Content>{renderChildren(children, close)}</Content>
            </Sheet>
        </SheetContainer>
    );
};

export default ChipSelectionSheet;
